import { AUTOMATED_EMAILS_ENABLED } from '../config/notifications';

const CLIENT_ROLES = ['client', 'seller', 'buyer', 'tenant', 'landlord'];
// Kelly's rule: nothing about listing expiration ever goes to a client automatically.
export const isExpirationItem = (item) => /expir/i.test(`${item?.name || ''} ${item?.subject || ''}`);

// Plans the automatic notifications from a task list.
// A Notification row with autoSend on goes out on its anchor date plus offsetDays.
// Nothing here sends anything: sending only happens through dispatchNotification,
// and only when NOTIFICATIONS_ENABLED is true and a sender is supplied.

const toDate = (value) => {
  if (!value) return null;
  const d = new Date(String(value).length === 10 ? `${value}T00:00:00` : value);
  return isNaN(d) ? null : d;
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

// Local calendar date as YYYY-MM-DD (avoids UTC shifting the day).
const toDay = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// tasks: the Notification rows of a task list.
// anchorDates: { underContractDate: '2026-11-02', moveInDate: '...', ... } taken from the transaction form.
// createdAt: the system timestamp saved when the transaction was created. Used for the 'created' anchor,
// so no one has to enter it by hand.
export const planNotifications = (tasks, anchorDates = {}, createdAt = null, transactionFields = {}) => {
  return (tasks || [])
    .filter((task) => task.type === 'Notification' && task.autoSend)
    // Skip anything that depends on a transaction answer that doesn't match (e.g., association approval = Yes).
    .filter((task) => !task.requiresField || transactionFields[task.requiresField] === task.requiresValue)
    .map((task) => {
      const anchor = toDate(task.anchor === 'created' ? createdAt : anchorDates[task.anchor]);
      if (!anchor) {
        return { name: task.name, status: 'missing_anchor', anchor: task.anchor };
      }
      return {
        name: task.name,
        sendOn: toDay(addDays(anchor, Number(task.offsetDays) || 0)),
        internalOwner: task.role,       // Agent, TC, or Marketing Manager
        clientSide: task.appliesTo,     // All, Seller, or Buyer
        templateId: task.templateId || null,
        subject: task.subject || '',
        body: task.body || '',
        recipients: task.recipients || [],
        // Who the email appears to be from, set automatically by who the task is assigned to:
        // Transaction Coordinator (TC) = Tina; otherwise the agent (and co-agent, if there is one).
        sender: task.role === 'TC' ? 'TC' : 'Agent',
        status: task.body ? 'ready' : 'needs_template',
      };
    });
};

// Items whose send date has arrived (today or earlier) and are ready to go.
export const dueNotifications = (plan, today = new Date()) => {
  const todayStr = toDay(today);
  return plan.filter((item) => item.status === 'ready' && item.sendOn && item.sendOn <= todayStr);
};

// Works out who gets a planned notification. Each recipient role on the task maps to people on the deal or team:
// Agent = the deal's agent, Co-Agent = co-agents on the form, Client = the client,
// and TC / Marketing Manager / Local Assistant = everyone with that role (passed in as usersByRole).
// Returns the people with an email, plus any role that could not be matched to anyone.
export const resolveRecipients = (item, { deal = {}, agent = null, usersByRole = {} } = {}) => {
  const people = [];
  const missing = [];
  const add = (role, name, email) => {
    if (email) people.push({ role, name: name || '', email });
    else missing.push(role);
  };
  for (const role of item.recipients || []) {
    if (role === 'Agent') {
      add(role, agent?.displayName, agent?.email);
    } else if (role === 'Co-Agent') {
      // No co-agent on the deal is normal, so only a co-agent with no email counts as missing.
      for (const a of [...(deal.coListAgents || []), ...(deal.coSellingAgents || [])]) add(role, a.name, a.email);
    } else if (role === 'Client') {
      add(role, `${deal.firstName || ''} ${deal.lastName || ''}`.trim(), deal.email);
    } else if (usersByRole[role]) {
      if (usersByRole[role].length === 0) missing.push(role);
      for (const u of usersByRole[role]) add(role, u.displayName, u.email);
    } else {
      missing.push(role);
    }
  }
  // One email per person, even if they hold more than one role on the list.
  const seen = new Set();
  const unique = people.filter((p) => {
    const key = p.email.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return { recipients: unique, missing: [...new Set(missing)] };
};

// Sends one planned item to each resolved recipient, one email per person.
// Nothing sends unless the master switch is on. sender(item, recipient) is supplied by the caller,
// so this file stays free of send code.
export const dispatchNotification = async (item, resolved, sender) => {
  if (!AUTOMATED_EMAILS_ENABLED) return { sent: false, reason: 'sending_disabled' };
  if (item.status !== 'ready') return { sent: false, reason: item.status };
  if (typeof sender !== 'function') return { sent: false, reason: 'no_sender' };
  if (!resolved?.recipients?.length) return { sent: false, reason: 'no_recipients', missing: resolved?.missing || [] };
  const results = [];
  for (const recipient of resolved.recipients) {
    if (isExpirationItem(item) && CLIENT_ROLES.includes(String(recipient.role || '').toLowerCase())) { results.push({ recipient, result: { sent: false, reason: 'expiration_never_to_client' } }); continue; }
    results.push({ recipient, result: await sender(item, recipient) });
  }
  return { sent: true, results, missing: resolved.missing || [] };
};
